import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Pagination from '@/components/base/Pagination';
import EmptyState from '@/components/base/EmptyState';
import { useSeo } from '@/hooks/useSeo';
import { newsList } from '@/mocks/news';
import { formatDate } from '@/utils/media';

const PAGE_SIZE = 5;

export default function News() {
  const categories = useMemo(() => {
    const set = new Set<string>();
    newsList.forEach((n) => set.add(n.category));
    return ['全部', ...Array.from(set)];
  }, []);
  const [category, setCategory] = useState('全部');
  const [page, setPage] = useState(1);

  useSeo({
    title: '影视资讯 - 天堂影视',
    description:
      '天堂影视资讯频道为你带来最新影讯、新片推荐与行业动态，涵盖票房、剧集定档、电影节及行业趋势，助你第一时间了解影视热点。',
    keywords: '天堂影视,影视资讯,影讯,新片推荐,行业动态',
  });

  const filtered = useMemo(() => {
    const list =
      category === '全部' ? newsList : newsList.filter((n) => n.category === category);
    return [...list].sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="container-page py-6">
      <nav className="flex items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600 cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">影视资讯</span>
      </nav>

      <div className="mt-4 rounded-lg bg-primary-950 px-5 py-6 text-background-50 md:px-7 md:py-8">
        <h1 className="text-2xl font-bold md:text-3xl">影视资讯</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-200/80">
          聚焦影视行业最新动态，为你带来影讯速递、新片推荐与深度观察，第一时间掌握值得关注的影视热点。
        </p>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            onClick={() => {
              setCategory(cat);
              setPage(1);
            }}
            className={`rounded-md border px-3 py-1.5 text-sm transition-colors cursor-pointer whitespace-nowrap ${
              category === cat
                ? 'border-primary-500 bg-primary-500 text-background-50'
                : 'border-background-300 bg-background-50 text-foreground-600 hover:border-primary-400 hover:text-primary-600'
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-5 space-y-4">
            {pageItems.map((news) => (
              <article
                key={news.id}
                className="group grid grid-cols-1 gap-4 overflow-hidden rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-300 sm:grid-cols-[220px_1fr]"
              >
                <Link
                  to={`/news/${news.id}`}
                  className="block h-40 w-full overflow-hidden rounded-md sm:h-32 cursor-pointer"
                >
                  <img
                    src={news.thumbnail}
                    alt={news.title}
                    title={`${news.title} - 天堂影视资讯`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                    <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-900">
                      {news.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {formatDate(news.date)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-newspaper-line"></i>
                      {news.source}
                    </span>
                  </div>
                  <h2 className="mt-2 text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    <Link to={`/news/${news.id}`} className="cursor-pointer">
                      {news.title}
                    </Link>
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
                    {news.summary}
                  </p>
                  <Link
                    to={`/news/${news.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent-600 cursor-pointer"
                  >
                    阅读全文
                    <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
          <Pagination
            current={current}
            totalPages={totalPages}
            onChange={(p) => {
              setPage(p);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </>
      ) : (
        <div className="mt-5">
          <EmptyState
            icon="ri-article-line"
            title="暂无资讯内容"
            description="该分类下暂时还没有资讯，换个分类看看吧。"
          />
        </div>
      )}
    </div>
  );
}